import { Button, EButtonType } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import { action, computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';
import {
    AssignUserType,
    AssignUserStatus,
    StateModalStatus,
} from 'common/interfaces/state';
import { ModalBottomBar } from 'components/content';

// Styles
const s = require('./StateAreaListPicker.css');

// Component
@inject('store')
@observer
export class StateAreaListPicker extends React.Component<IAppOnlyStore, {}> {
    private noOneOptionValue: string = '0/0';
    private noOneOptionLabel: string = 'No one';

    @observable private selectedAreaRole: AssignUserType | null = null;
    @observable private selectedUser: {
        value: string | null;
        id: number | null;
        name: string | null;
        slpcode: number | null;
    } = {
        value: null,
        id: null,
        name: null,
        slpcode: null,
    };

    @computed private get usersOptions(): Array<{
        value: string;
        label: string;
    }> {
        const {
            state: { checkedAreasSearchedResultsLength },
        } = this.props.store;

        return [
            {
                value: 'label',
                label:
                    'Assign selected area' +
                    (checkedAreasSearchedResultsLength > 1 ? 's' : '') +
                    ' to',
            },
            ...toJS(
                this.props.store.users.allActiveSalesUsers.filter(
                    user =>
                        typeof user.id !== 'undefined' &&
                        typeof user.slpcode !== 'undefined' &&
                        user.slpcode !== null
                )
            ).map(user => ({
                value: user.id.toString() + '/' + user.slpcode.toString(),
                label: user.name,
            })),
            ...[
                {
                    value: this.noOneOptionValue,
                    label: this.noOneOptionLabel,
                },
            ],
        ];
    }

    @computed
    private get areaRolesOptions(): Array<{ value: string; label: string }> {
        if (this.selectedUser.id === 0) {
            return [
                {
                    value: 'label',
                    label: 'Select user to clear',
                },
                {
                    value: 'primary',
                    label: 'Clear primary sales rep',
                },
                {
                    value: 'secondary',
                    label: 'Clear secondary sales rep',
                },
                {
                    value: 'both',
                    label: 'Clear both sales reps',
                },
            ];
        }

        return [
            {
                value: 'label',
                label: 'Select user role',
            },
            {
                value: 'primary',
                label: 'Set as primary sales rep',
            },
            {
                value: 'secondary',
                label: 'Set as secondary sales rep',
            },
            {
                value: 'sole',
                label: 'Set as only rep for the area (primary)',
            },
        ];
    }

    @computed
    private get showBottomBar(): boolean {
        const { state } = this.props.store;

        return (
            state.checkedAreasSearchedResultsLength > 0 ||
            state.assignUser.status !== AssignUserStatus.None
        );
    }

    public render() {
        const {
            state: { modalStatus, assignUser },
        } = this.props.store;

        if (modalStatus !== StateModalStatus.OpenOnSpecificArea) {
            return null;
        }

        return (
            <ModalBottomBar
                show={this.showBottomBar}
                classNameForContent={s.container}
            >
                <div>
                    <select
                        value={this.selectedUser.value || 'label'}
                        onChange={this.handleUserChange}
                    >
                        {this.usersOptions.map((option, i) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <select
                        value={this.selectedAreaRole || 'label'}
                        onChange={this.handleRoleChange}
                    >
                        {this.areaRolesOptions.map((option, i) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.status}>
                    {(assignUser.status === AssignUserStatus.Error && (
                        <p className={s.error}>
                            {
                                'Could not change sales rep assigned to selected area(s), please try again'
                            }
                        </p>
                    )) || (
                        <SavingIndicator
                            labels={{
                                saving: 'Assigning sales rep...',
                                error: 'Could not assign rep, please try again',
                                success: 'Assigned rep to customers',
                            }}
                            status={
                                assignUser.status === AssignUserStatus.Saving
                                    ? SavingIndicatorStatus.Saving
                                    : assignUser.status ===
                                      AssignUserStatus.Error
                                    ? SavingIndicatorStatus.Error
                                    : assignUser.status ===
                                      AssignUserStatus.Success
                                    ? SavingIndicatorStatus.Success
                                    : SavingIndicatorStatus.None
                            }
                        />
                    )}
                </div>
            </ModalBottomBar>
        );
    }

    @action
    private handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val: string = e.currentTarget.value;

        const split = val.split('/');
        if (split.length >= 2) {
            const userId: number = parseInt(split[0], 10);
            const userSlpcode: number = parseInt(split[1], 10);

            let userName: string = '';
            this.usersOptions.some((user, i) => {
                if (user.value === val) {
                    userName = user.label;
                    return true;
                }
            });

            if (val === this.noOneOptionValue) {
                userName = this.noOneOptionLabel;
            }

            this.selectedUser.value = val;
            this.selectedUser.id = userId;
            this.selectedUser.slpcode = userSlpcode;
            this.selectedUser.name = userName;
            this.selectedAreaRole = null;
        }
    };

    @action
    private handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val: string = e.currentTarget.value;
        if (val !== 'label') {
            this.selectedAreaRole = val as AssignUserType;

            if (this.selectedUser.id !== null) {
                this.submit();
            }
        }
    };

    private handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        this.submit();
    };

    private submit = async () => {
        if (
            this.selectedUser.id !== null &&
            this.selectedUser.slpcode !== null &&
            this.selectedAreaRole &&
            this.props.store.state.assignUser.status !== AssignUserStatus.Saving
        ) {
            try {
                if (this.selectedAreaRole === 'both') {
                    await this.props.store.state.assignUserToCheckedAreas(
                        this.selectedUser.id,
                        this.selectedUser.slpcode,
                        this.selectedUser.name,
                        true
                    );

                    await this.props.store.state.assignUserToCheckedAreas(
                        this.selectedUser.id,
                        this.selectedUser.slpcode,
                        this.selectedUser.name,
                        false
                    );

                    this.clear();

                    return;
                }

                if (this.selectedAreaRole === 'sole') {
                    await this.props.store.state.assignUserToCheckedAreas(
                        this.selectedUser.id,
                        this.selectedUser.slpcode,
                        this.selectedUser.name,
                        true
                    );

                    await this.props.store.state.assignUserToCheckedAreas(
                        0,
                        0,
                        this.noOneOptionLabel,
                        false
                    );

                    this.clear();

                    return;
                }

                await this.props.store.state.assignUserToCheckedAreas(
                    this.selectedUser.id,
                    this.selectedUser.slpcode,
                    this.selectedUser.name,
                    this.selectedAreaRole === 'primary' ? true : false
                );

                this.clear();
            } catch (err) {
                return null;
            }
        }
    };

    @action
    private clear = () => {
        this.selectedUser.value = null;
        this.selectedUser.id = null;
        this.selectedUser.slpcode = null;
        this.selectedUser.name = null;
        this.selectedAreaRole = null;

        this.props.store.state.toggleAllAreaCheckboxes(false);
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/state/StateAreaListPicker.tsx